import { type Anchor, type BoardState, type ItemOf } from '../board'
import { union, type Vec } from './geometry'
import { freeSpotNear } from './placement'

// Messages on the board that reply to each other form a thread; messages can
// point at things (an anchor). Both are explicit data on the message
// (`replyTo`, `anchor`): nothing here is inferred from positions.

type Message = ItemOf<'message'>

export type Thread = {
  root: Message
  /** Every message below the root, in the order they were said. */
  replies: Message[]
}

const asMessage = (s: BoardState, id: string | null | undefined): Message | null => {
  const it = id ? s.items.get(id) : undefined
  return it?.kind === 'message' ? it : null
}

/** The first message of the thread `id` is in (itself when it replies to nothing still on the board). */
export function threadRootOf(s: BoardState, id: string): string {
  let cur = asMessage(s, id)
  const seen = new Set<string>()
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id)
    const parent = asMessage(s, cur.data.replyTo)
    if (!parent) return cur.id
    cur = parent
  }
  return id
}

/** Every thread on the board, keyed by root id. A message with no replies is a thread of one. */
export function threads(s: BoardState): Map<string, Thread> {
  const out = new Map<string, Thread>()
  const messages = [...s.items.values()]
    .filter((i): i is Message => i.kind === 'message')
    .sort((a, b) => a.createdAt - b.createdAt)
  for (const m of messages) {
    const rootId = threadRootOf(s, m.id)
    const root = asMessage(s, rootId)
    if (!root) continue
    const t = out.get(rootId) ?? { root, replies: [] }
    if (m.id !== rootId) t.replies.push(m)
    out.set(rootId, t)
  }
  return out
}

/** Everything the board derives from messages, computed once per board state. */
export type ThreadIndex = {
  threads: Map<string, Thread>
  /** Each message's thread root. */
  rootOf: Map<string, string>
  /** For each item, the messages pointing at it. */
  comments: Map<string, string[]>
}

export function indexThreads(s: BoardState): ThreadIndex {
  const all = threads(s)
  const rootOf = new Map<string, string>()
  for (const [rootId, t] of all) {
    rootOf.set(rootId, rootId)
    for (const r of t.replies) rootOf.set(r.id, rootId)
  }
  const comments = new Map<string, string[]>()
  for (const i of s.items.values()) {
    if (i.kind !== 'message') continue
    for (const target of i.data.anchor?.items ?? []) {
      const list = comments.get(target) ?? []
      list.push(i.id)
      comments.set(target, list)
    }
  }
  return { threads: all, rootOf, comments }
}

/**
 * Replies hidden because their thread is folded. Threads start open; folding
 * is each viewer's own choice (`folded`: root ids), never board data.
 */
export function hiddenReplies(index: ThreadIndex, folded: ReadonlySet<string>): Set<string> {
  const hidden = new Set<string>()
  for (const rootId of folded) {
    for (const r of index.threads.get(rootId)?.replies ?? []) hidden.add(r.id)
  }
  return hidden
}

/** Height of the replies line a thread's first message grows once it has replies. */
export const THREAD_FOOTER_H = 30

/**
 * Where a new reply to `parentId` goes: under the thread's lowest message,
 * aligned with its root. A first reply leaves room for the replies line the
 * root is about to grow: nothing moves afterwards to make room.
 */
export function replySpot(s: BoardState, parentId: string, w: number, h: number): Vec {
  const t = threads(s).get(threadRootOf(s, parentId))
  const parent = asMessage(s, parentId)
  if (!t || !parent) return { x: 0, y: 0 }
  const all = [t.root, ...t.replies]
  const lowest = Math.max(...all.map((m) => m.y + m.h))
  const room = t.replies.length ? 0 : THREAD_FOOTER_H
  return freeSpotNear(s, { x: t.root.x, y: lowest + 12 + room }, w, h, 8)
}

/** Where a message about `anchor` first appears: to the right of what it points at, top-aligned. */
export function anchorSpot(
  s: BoardState,
  anchor: Anchor,
  w: number,
  h: number,
  near?: { y: number },
): Vec {
  const targets = anchor.items.map((id) => s.items.get(id)).filter((i) => !!i)
  const box = union(targets)
  if (!box) return { x: 0, y: 0 }
  const y = near ? Math.max(box.y, Math.min(near.y - 16, box.y + box.h - h)) : box.y
  return freeSpotNear(s, { x: box.x + box.w + 40, y: Math.round(y) }, w, h, 12)
}
